import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import {
    SovosBox,
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosDialogTitle,
    SovosIconButton,
    SovosMenuItem,
    SovosSelect,
    SovosSnackbar,
    SovosSwitch,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { Add, Close, Delete, Edit, PlayArrow } from "mosaic-react-icons";
import TranslationModal from "./TranslationModal";
import { formatConditionSummary } from "./featureFlags";

const STATE_LABELS = {
    Active: { label: "Active", dot: "success.main", bg: "success.light", text: "success.dark" },
    Inactive: { label: "Inactive", dot: "text.disabled", bg: "grey.100", text: "text.secondary" },
    Processing: { label: "Processing", dot: "primary.main", bg: "primary.light", text: "primary.dark" },
};

const STATUS_FILTERS = ["All", "Active", "Inactive", "Processing"];

const formatTimestamp = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString();
    } catch {
        return iso;
    }
};

const StateBadge = ({ state }) => {
    const cfg = STATE_LABELS[state] || STATE_LABELS.Inactive;
    return (
        <SovosBox
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: 2,
                bgcolor: cfg.bg,
            }}
        >
            <SovosBox sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot }} />
            <SovosTypography variant="caption" sx={{ color: cfg.text, fontWeight: 600 }}>
                {cfg.label}
            </SovosTypography>
        </SovosBox>
    );
};

const TranslationsList = ({
    profileId,
    projectId,
    columnOptions = [],
    canMutate = true,
    nonOwnerTooltip = "Only the project owner can manage translations.",
    onList,
    onCreate,
    onUpdate,
    onToggleState,
    onSoftDelete,
    onApply,
}) => {
    const [translations, setTranslations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("All");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [notification, setNotification] = useState(null);
    const previousStatesRef = React.useRef(new Map());

    const refresh = useCallback(async () => {
        if (!onList || !profileId || !projectId) return;
        setLoading(true);
        try {
            const result = await onList(profileId, projectId);
            const next = Array.isArray(result) ? result : [];
            const prev = previousStatesRef.current;
            const transitions = [];
            for (const t of next) {
                const prevState = prev.get(t.id);
                if (prevState && prevState !== t.state) {
                    if (t.state === "Processing") {
                        transitions.push({ type: "processing", translation: t });
                    } else if (prevState === "Processing" && t.state === "Active") {
                        transitions.push({ type: "active", translation: t });
                    }
                }
                prev.set(t.id, t.state);
            }
            if (transitions.length === 1) {
                const tr = transitions[0];
                if (tr.type === "processing") {
                    setNotification({ type: "info", message: `"${tr.translation.name}" is processing…` });
                } else {
                    const count = tr.translation.last_run_changed_rows ?? 0;
                    setNotification({ type: "confirmation", message: `"${tr.translation.name}" finished — ${count} row(s) updated.` });
                }
            }
            setTranslations(next);
        } finally {
            setLoading(false);
        }
    }, [onList, profileId, projectId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const hasProcessing = translations.some((t) => t.state === "Processing");
        if (!hasProcessing) return undefined;
        const handle = setInterval(refresh, 6000);
        return () => clearInterval(handle);
    }, [translations, refresh]);

    const filtered = useMemo(() => {
        if (statusFilter === "All") return translations;
        return translations.filter((t) => t.state === statusFilter);
    }, [translations, statusFilter]);

    const handleOpenCreate = () => {
        setEditing(null);
        setSubmitError(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (translation) => {
        setEditing(translation);
        setSubmitError(null);
        setModalOpen(true);
    };

    const handleSubmit = async (values) => {
        if (!canMutate) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            if (editing) {
                const result = await onUpdate(editing.id, values);
                if (!result) {
                    setSubmitError("Could not save the translation. Please try again.");
                    return;
                }
            } else {
                const result = await onCreate({
                    projectId,
                    importProfileId: profileId,
                    ...values,
                });
                if (!result) {
                    setSubmitError("Could not create the translation. Please try again.");
                    return;
                }
            }
            setModalOpen(false);
            await refresh();
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggle = async (translation) => {
        if (!canMutate || !onToggleState) return;
        const next = translation.state === "Active" ? "Inactive" : "Active";
        await onToggleState(translation.id, next);
        await refresh();
    };

    const handleApply = async (translation) => {
        if (!canMutate || !onApply) return;
        const result = await onApply(translation.id, profileId, projectId);
        if (result?.queued === true) {
            setNotification({ type: "info", message: `Apply queued for "${translation.name}".` });
        } else {
            setNotification({ type: "error", message: `Could not queue "${translation.name}".` });
        }
        await refresh();
    };

    const handleDeleteConfirm = async () => {
        if (!canMutate || !onSoftDelete || !pendingDelete) return;
        const target = pendingDelete;
        setPendingDelete(null);
        await onSoftDelete(target.id);
        await refresh();
    };

    return (
        <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>Status</SovosTypography>
                    <SovosSelect
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 120 }}
                    >
                        {STATUS_FILTERS.map((s) => (
                            <SovosMenuItem key={s} value={s}>{s}</SovosMenuItem>
                        ))}
                    </SovosSelect>
                </SovosBox>
                <SovosTooltip title={canMutate ? "" : nonOwnerTooltip} placement="top">
                    <span>
                        <SovosButton
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleOpenCreate}
                            disabled={!canMutate}
                        >
                            New Translation
                        </SovosButton>
                    </span>
                </SovosTooltip>
            </SovosBox>

            {loading && (
                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>Loading translations…</SovosTypography>
                </SovosBox>
            )}

            {!loading && filtered.length === 0 && (
                <SovosBox sx={{
                    p: 3,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    textAlign: "center",
                }}>
                    <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                        {translations.length === 0
                            ? "No translations yet. Use New Translation to create your first rule."
                            : `No translations match "${statusFilter}".`}
                    </SovosTypography>
                </SovosBox>
            )}

            {!loading && filtered.length > 0 && (
                <SovosBox sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                }}>
                    <SovosBox sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(180px, 1.4fr) minmax(220px, 2fr) 180px 140px 120px 160px",
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: "grey.50",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>Name</SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>Condition</SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>Last run</SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>Fixes applied</SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700 }}>State</SovosTypography>
                        <SovosTypography variant="caption" sx={{ fontWeight: 700, textAlign: "right" }}>Actions</SovosTypography>
                    </SovosBox>
                    {filtered.map((t) => {
                        const sameColumn = translations.filter(
                            (other) => other.id !== t.id && other.column_name === t.column_name && other.state === "Active",
                        );
                        const stateLabel = STATE_LABELS[t.state] || STATE_LABELS.Inactive;
                        return (
                            <SovosBox
                                key={t.id}
                                data-testid={`translation-row-${t.id}`}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(180px, 1.4fr) minmax(220px, 2fr) 180px 140px 120px 160px",
                                    gap: 1,
                                    px: 2,
                                    py: 1.25,
                                    alignItems: "center",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    "&:last-of-type": { borderBottom: 0 },
                                }}
                            >
                                <SovosBox>
                                    <SovosTypography variant="body2" sx={{ fontWeight: 600 }}>
                                        {t.name}
                                    </SovosTypography>
                                    <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                        {t.source === "ai_suggested" ? "AI suggested" : "Manual"}
                                    </SovosTypography>
                                </SovosBox>
                                <SovosBox>
                                    <SovosTypography variant="body2">{formatConditionSummary(t)}</SovosTypography>
                                    {sameColumn.length > 0 && (
                                        <SovosTooltip
                                            title="Multiple translations target this column. Order-sensitive chains may produce unexpected results."
                                            placement="top"
                                        >
                                            <SovosTypography variant="caption" sx={{ color: "warning.dark" }}>
                                                shares column with {sameColumn.length} other rule{sameColumn.length !== 1 ? "s" : ""}
                                            </SovosTypography>
                                        </SovosTooltip>
                                    )}
                                </SovosBox>
                                <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                    {formatTimestamp(t.last_run_at)}
                                </SovosTypography>
                                <SovosTypography variant="caption">
                                    {t.last_run_changed_rows == null ? "—" : t.last_run_changed_rows}
                                </SovosTypography>
                                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {t.state === "Processing" ? (
                                        <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <CircularProgress size={12} />
                                            <StateBadge state={t.state} />
                                        </SovosBox>
                                    ) : (
                                        <SovosTooltip title={canMutate ? "" : nonOwnerTooltip} placement="top">
                                            <span>
                                                <SovosSwitch
                                                    checked={t.state === "Active"}
                                                    onChange={() => handleToggle(t)}
                                                    disabled={!canMutate}
                                                    aria-label={`Toggle ${t.name}`}
                                                />
                                            </span>
                                        </SovosTooltip>
                                    )}
                                    <SovosTypography variant="caption" sx={{ color: stateLabel.text }}>
                                        {stateLabel.label}
                                    </SovosTypography>
                                </SovosBox>
                                <SovosBox sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                                    <SovosTooltip title={canMutate ? "Apply now" : nonOwnerTooltip} placement="top">
                                        <span>
                                            <SovosIconButton
                                                onClick={() => handleApply(t)}
                                                disabled={!canMutate || t.state === "Processing"}
                                                tooltipText="Apply now"
                                                size="small"
                                                aria-label={`Apply ${t.name}`}
                                                data-testid={`translation-${t.id}-apply`}
                                            >
                                                <PlayArrow fontSize="small" />
                                            </SovosIconButton>
                                        </span>
                                    </SovosTooltip>
                                    <SovosTooltip title={canMutate ? "Edit" : nonOwnerTooltip} placement="top">
                                        <span>
                                            <SovosIconButton
                                                onClick={() => handleOpenEdit(t)}
                                                disabled={!canMutate}
                                                tooltipText="Edit"
                                                size="small"
                                                aria-label={`Edit ${t.name}`}
                                                data-testid={`translation-${t.id}-edit`}
                                            >
                                                <Edit fontSize="small" />
                                            </SovosIconButton>
                                        </span>
                                    </SovosTooltip>
                                    <SovosTooltip title={canMutate ? "Delete" : nonOwnerTooltip} placement="top">
                                        <span>
                                            <SovosIconButton
                                                onClick={() => setPendingDelete(t)}
                                                disabled={!canMutate}
                                                tooltipText="Delete"
                                                size="small"
                                                aria-label={`Delete ${t.name}`}
                                                data-testid={`translation-${t.id}-delete`}
                                            >
                                                <Delete fontSize="small" />
                                            </SovosIconButton>
                                        </span>
                                    </SovosTooltip>
                                </SovosBox>
                            </SovosBox>
                        );
                    })}
                </SovosBox>
            )}

            <TranslationModal
                open={modalOpen}
                mode={editing ? "edit" : "create"}
                initialValues={editing}
                columnOptions={columnOptions}
                onCancel={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                submitting={submitting}
                submitError={submitError}
            />

            <SovosDialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
                <SovosDialogTitle>
                    Delete translation
                    <SovosIconButton
                        onClick={() => setPendingDelete(null)}
                        tooltipText="Close"
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <Close />
                    </SovosIconButton>
                </SovosDialogTitle>
                <SovosDialogContent>
                    <SovosTypography variant="body2" sx={{ mb: 1 }}>
                        Are you sure you want to delete "{pendingDelete?.name}"?
                    </SovosTypography>
                    <SovosTypography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
                        This does not revert data already transformed.
                    </SovosTypography>
                </SovosDialogContent>
                <SovosDialogActions>
                    <SovosButton onClick={() => setPendingDelete(null)} variant="text">Cancel</SovosButton>
                    <SovosButton onClick={handleDeleteConfirm} variant="contained" color="error">
                        Delete
                    </SovosButton>
                </SovosDialogActions>
            </SovosDialog>

            <SovosSnackbar
                open={!!notification}
                message={notification?.message}
                type={notification?.type}
                onClose={() => setNotification(null)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </SovosBox>
    );
};

export default TranslationsList;
